import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, IconButton, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { useTheme } from '@mui/material/styles';
import MermaidDiagram from './MermaidDiagram';

/**
 * CodeBlock component with copy button for code blocks in markdown
 * Displays code with syntax highlighting styles and a copy-to-clipboard button
 * Special handling for Mermaid diagrams
 */
const CodeBlock = ({ children, className, inline, ...props }) => {
  const theme = useTheme();
  const colors = theme.chat;
  const [copied, setCopied] = useState(false);
  // ✅ FIX: Track timeout for cleanup to prevent setState after unmount
  const copyTimeoutRef = useRef(null);

  // ✅ FIX: Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  // Extract language from className (e.g., "language-javascript" -> "javascript")
  const language = className ? className.replace('language-', '') : null;

  // Get the actual code content
  const code = String(children).replace(/\n$/, '');

  // ✅ Special handling for Mermaid diagrams
  if (language === 'mermaid' && !inline) {
    return <MermaidDiagram code={code} />;
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      // ✅ FIX: Clear any existing timeout and track new one
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
      copyTimeoutRef.current = setTimeout(() => {
        setCopied(false);
        copyTimeoutRef.current = null;
      }, 2000);
    } catch (err) {
      console.error('Failed to copy code:', err);
    }
  };

  // Inline code (single backtick) - no copy button
  if (inline) {
    return (
      <code
        style={{
          backgroundColor: colors.markdown.codeInline.background,
          color: colors.markdown.codeInline.text,
          padding: '0.1em 0.3em',
          borderRadius: '3px',
          fontFamily: '"Roboto Mono", monospace',
          fontSize: '0.9em'
        }}
        {...props}
      >
        {children}
      </code>
    );
  }

  // Block code (triple backtick) - with copy button
  return (
    <Box
      sx={{
        position: 'relative',
        borderRadius: '4px',
        margin: '0.5em 0',
        backgroundColor: colors.markdown.codeBlock.background,
        '&:hover .copy-button': {
          opacity: 1
        }
      }}
    >
      {/* Language label (optional) */}
      {language && (
        <Box
          sx={{
            px: 1.5,
            py: 0.5,
            fontSize: '0.75rem',
            color: colors.markdown.codeBlock.text,
            opacity: 0.6,
            fontFamily: '"Roboto Mono", monospace',
            borderBottom: `1px solid ${colors.markdown.table.border}`,
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}
        >
          {language}
        </Box>
      )}

      {/* Copy button */}
      <Tooltip title={copied ? 'Copied!' : 'Copy code'} arrow placement="left">
        <IconButton
          className="copy-button"
          onClick={handleCopy}
          size="small"
          sx={{
            position: 'absolute',
            top: language ? 8 : 8,
            right: 8,
            opacity: 0,
            transition: 'opacity 0.2s ease-in-out',
            color: colors.markdown.codeBlock.text,
            bgcolor: `${colors.markdown.codeBlock.background}cc`,
            backdropFilter: 'blur(4px)',
            '&:hover': {
              bgcolor: colors.markdown.codeBlock.text,
              color: colors.markdown.codeBlock.background
            },
            zIndex: 10,
            '@media (hover: none)': {
              // Always show on touch devices
              opacity: 0.7
            }
          }}
          aria-label="Copy code to clipboard"
        >
          {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
        </IconButton>
      </Tooltip>

      {/* Code content */}
      <pre
        style={{
          backgroundColor: colors.markdown.codeBlock.background,
          color: colors.markdown.codeBlock.text,
          padding: '0.75em',
          paddingTop: language ? '0.5em' : '0.75em',
          borderRadius: '4px',
          overflowX: 'auto',
          margin: 0,
          fontFamily: '"Roboto Mono", monospace',
          fontSize: '0.875rem',
          lineHeight: 1.5
        }}
      >
        <code
          className={className}
          style={{
            backgroundColor: 'transparent',
            padding: 0,
            fontFamily: 'inherit',
            fontSize: 'inherit'
          }}
          {...props}
        >
          {children}
        </code>
      </pre>
    </Box>
  );
};

CodeBlock.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  inline: PropTypes.bool
};

export default CodeBlock;
